// File: /var/www/tpcrm/frontend/src/components/Agent/AgentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  analyticsService, 
  clientsService, 
  quotesService, 
  commissionsService,
  itinerariesService,
  paymentRemindersService 
} from '../../services';

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data using services
      const [dashboard, clientsData, quotesData, commissionsData, itinerariesData, remindersData] = await Promise.all([
        analyticsService.getDashboard(),
        clientsService.getAll(),
        quotesService.getAll(),
        commissionsService.getAll(),
        itinerariesService.getAll(),
        paymentRemindersService.getAll()
      ]);

      setDashboardData(dashboard);
      setClients(clientsData);
      setQuotes(quotesData);
      setCommissions(commissionsData);
      setItineraries(itinerariesData);
      setPaymentReminders(remindersData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (quoteData) => {
    try {
      const newQuote = await quotesService.create(quoteData);
      setQuotes([...quotes, newQuote]);
      return newQuote;
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  };

  const handleUpdateQuote = async (quoteId, quoteData) => {
    try {
      const updatedQuote = await quotesService.update(quoteId, quoteData);
      setQuotes(quotes.map(q => q.id === quoteId ? updatedQuote : q));
      return updatedQuote;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  };

  const handleSendQuote = async (quoteId, emailData) => {
    try {
      await quotesService.send(quoteId, emailData);
      // Update quote status locally
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, status: 'sent' } : q
      ));
    } catch (error) {
      console.error('Error sending quote:', error);
      throw error;
    }
  };

  const handleConvertToItinerary = async (quoteId) => {
    try {
      const newItinerary = await quotesService.convertToItinerary(quoteId);
      // Update local state
      setItineraries([...itineraries, newItinerary]);
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, status: 'converted' } : q
      ));
      return newItinerary;
    } catch (error) {
      console.error('Error converting quote:', error);
      throw error;
    }
  };

  const handleSubmitCommission = async (commissionData) => {
    try {
      const newCommission = await commissionsService.submit(commissionData);
      setCommissions([...commissions, newCommission]);
      return newCommission;
    } catch (error) {
      console.error('Error submitting commission:', error);
      throw error;
    }
  };

  const handleCreatePaymentReminder = async (reminderData) => {
    try {
      const newReminder = await paymentRemindersService.create(reminderData);
      setPaymentReminders([...paymentReminders, newReminder]);
      return newReminder;
    } catch (error) {
      console.error('Error creating payment reminder:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Dashboard Overview</h2>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="ml-5">
                    <p className="text-gray-500 text-sm">Total Clients</p>
                    <p className="text-2xl font-semibold text-gray-900">{clients.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="ml-5">
                    <p className="text-gray-500 text-sm">Active Quotes</p>
                    <p className="text-2xl font-semibold text-gray-900">{quotes.filter(q => q.status === 'draft' || q.status === 'sent').length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="ml-5">
                    <p className="text-gray-500 text-sm">Active Itineraries</p>
                    <p className="text-2xl font-semibold text-gray-900">{itineraries.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5">
                    <p className="text-gray-500 text-sm">Pending Commissions</p>
                    <p className="text-2xl font-semibold text-gray-900">{commissions.filter(c => c.status === 'pending').length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => navigate('/agent/quotes/new')}
                  className="bg-blue-600 text-white rounded-lg p-4 hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Quote
                </button>
                <button
                  onClick={() => navigate('/agent/clients')}
                  className="bg-gray-600 text-white rounded-lg p-4 hover:bg-gray-700 transition-colors flex items-center justify-center"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Manage Clients
                </button>
                <button
                  onClick={() => navigate('/agent/commissions')}
                  className="bg-green-600 text-white rounded-lg p-4 hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Submit Commission
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h3 className="text-lg font-medium mb-4">Recent Activity</h3>
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {quotes.slice(0, 5).map(quote => (
                    <li key={quote.id}>
                      <Link to={`/agent/quotes/${quote.id}`} className="block hover:bg-gray-50 px-4 py-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Quote #{quote.quote_number || quote.id} - {quote.client_name || 'Unknown Client'}
                            </p>
                            <p className="text-sm text-gray-500">
                              ${(parseFloat(quote.total_amount) || 0).toFixed(2)} • {new Date(quote.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            quote.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                            quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {quote.status}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
